/**
 * Extrai texto de um PDF no browser via pdfjs-dist.
 * Reconstrói linhas pela coordenada Y (pdf.js entrega fragments soltos).
 */

type PdfTextItem = {
  str?: string;
  transform?: number[];
};

const Y_TOLERANCE = 2.5;

/**
 * Agrupa items do getTextContent em linhas legíveis (topo → base, esq → dir).
 * Sem transform, faz fallback juntando com espaço.
 */
export function reconstructPdfTextLines(items: PdfTextItem[]): string[] {
  const usable = items
    .map((item) => {
      const str = item.str ?? "";
      if (!str) return null;
      const t = item.transform;
      const hasPos = Array.isArray(t) && t.length >= 6;
      return {
        str,
        x: hasPos ? Number(t![4]) : 0,
        y: hasPos ? Number(t![5]) : NaN,
      };
    })
    .filter((x): x is { str: string; x: number; y: number } => x != null);

  if (usable.length === 0) return [];

  const withPos = usable.filter((i) => Number.isFinite(i.y));
  if (withPos.length === 0) {
    const joined = usable
      .map((i) => i.str)
      .join("")
      .replace(/\s+/g, " ")
      .trim();
    return joined ? [joined] : [];
  }

  const sorted = [...withPos].sort((a, b) => b.y - a.y || a.x - b.x);
  const groups: { y: number; parts: { str: string; x: number }[] }[] = [];

  for (const item of sorted) {
    const last = groups[groups.length - 1];
    if (!last || Math.abs(last.y - item.y) > Y_TOLERANCE) {
      groups.push({ y: item.y, parts: [{ str: item.str, x: item.x }] });
    } else {
      last.parts.push({ str: item.str, x: item.x });
    }
  }

  return groups
    .map((g) =>
      g.parts
        .sort((a, b) => a.x - b.x)
        .map((p) => p.str)
        .join("")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter((line) => line.length > 0);
}

export async function extractPdfText(file: File | Blob): Promise<string> {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const pages: string[] = [];

  for (let pageNum = 1; pageNum <= doc.numPages; pageNum += 1) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();
    const lines = reconstructPdfTextLines(
      content.items as PdfTextItem[],
    );
    pages.push(lines.join("\n"));
  }

  return pages.join("\n");
}
